import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Heart, 
  Users, 
  Brain, 
  Briefcase, 
  DollarSign, 
  Vote, 
  HandHeart,
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  ChevronRight,
  ArrowRight,
  LogOut,
  Settings,
  Bell,
  Inbox,
  CreditCard,
  UserPlus,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  LayoutDashboard
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_family-stability-hub/artifacts/kma6vbfh_Family%20Life%20Lifters%20Color%20Logo%202.png";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Admin Login Page
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role === 'admin') {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const userData = await login(email, password);
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        setError('Admin access required');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="Family Life Lifters" className="h-16 mx-auto mb-4" />
          <CardTitle className="font-heading text-2xl text-accent">Admin Login</CardTitle>
          <CardDescription>Sign in to access the dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary-hover"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <a href="/" className="text-sm text-muted-foreground hover:text-primary">
              ← Back to website
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [donations, setDonations] = useState([]);
  const [users, setUsers] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, inquiriesRes, donationsRes, usersRes, alertsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/inquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/donations`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/alert-settings`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setInquiries(inquiriesRes.data);
      setDonations(donationsRes.data);
      setUsers(usersRes.data);
      setAlertSettings(alertsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}/status`, { status }, { withCredentials: true });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const addAlertEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    const updated = [...(alertSettings?.alert_emails || []), newEmail];
    try {
      await axios.put(`${API}/admin/alert-settings`, {
        ...alertSettings,
        alert_emails: updated
      }, { withCredentials: true });
      setAlertSettings({ ...alertSettings, alert_emails: updated });
      setNewEmail('');
      toast.success('Email added');
    } catch (error) {
      toast.error('Failed to add email');
    }
  };

  const removeAlertEmail = async (email) => {
    const updated = alertSettings.alert_emails.filter(e => e !== email);
    try {
      await axios.put(`${API}/admin/alert-settings`, {
        ...alertSettings,
        alert_emails: updated
      }, { withCredentials: true });
      setAlertSettings({ ...alertSettings, alert_emails: updated });
      toast.success('Email removed');
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, newUser, { withCredentials: true });
      toast.success('Admin user created');
      setNewUser({ email: '', password: '', name: '' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={styles[status] || styles.new}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Logo" className="h-10" />
              <span className="font-heading font-bold text-accent">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">
              <Inbox className="w-4 h-4 mr-2" />Inquiries
            </TabsTrigger>
            <TabsTrigger value="donations" data-testid="tab-donations">
              <CreditCard className="w-4 h-4 mr-2" />Donations
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Inquiries</p>
                      <p className="text-3xl font-bold text-accent">{stats?.total_inquiries || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Inbox className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">New Inquiries</p>
                      <p className="text-3xl font-bold text-primary">{stats?.new_inquiries || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Donations</p>
                      <p className="text-3xl font-bold text-accent">{stats?.total_donations || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Amount Raised</p>
                      <p className="text-3xl font-bold text-green-600">${stats?.total_donation_amount?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Recent Inquiries</CardTitle>
                </CardHeader>
                <CardContent>
                  {inquiries.slice(0, 5).map((inq) => (
                    <div key={inq.id} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{inq.first_name} {inq.last_name}</p>
                        <p className="text-sm text-muted-foreground">{inq.program_interest}</p>
                      </div>
                      {getStatusBadge(inq.status)}
                    </div>
                  ))}
                  {inquiries.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No inquiries yet</p>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Recent Donations</CardTitle>
                </CardHeader>
                <CardContent>
                  {donations.filter(d => d.payment_status === 'paid').slice(0, 5).map((don) => (
                    <div key={don.id} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{don.donor_name}</p>
                        <p className="text-sm text-muted-foreground">{don.donor_email}</p>
                      </div>
                      <span className="font-bold text-green-600">${don.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                  {donations.filter(d => d.payment_status === 'paid').length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No donations yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Program Inquiries</CardTitle>
                <CardDescription>Manage and respond to program inquiries</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {inquiries.map((inq) => (
                    <div key={inq.id} className="border rounded-lg p-4 bg-white" data-testid={`inquiry-${inq.id}`}>
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-heading font-semibold">{inq.first_name} {inq.last_name}</h4>
                            {getStatusBadge(inq.status)}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                            <p><Mail className="inline w-4 h-4 mr-1" />{inq.email}</p>
                            {inq.phone && <p><Phone className="inline w-4 h-4 mr-1" />{inq.phone}</p>}
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground mb-2">
                            <span><strong>Program:</strong> {inq.program_interest}</span>
                            <span><strong>Referral:</strong> {inq.referral_source}</span>
                          </div>
                          <p className="text-sm bg-stone-50 p-3 rounded">{inq.message}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Select value={inq.status} onValueChange={(val) => updateInquiryStatus(inq.id, val)}>
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                  {inquiries.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No inquiries yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Donations Tab */}
          <TabsContent value="donations">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Donation History</CardTitle>
                <CardDescription>View all donation transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Donor</th>
                        <th className="text-left py-3 px-4">Email</th>
                        <th className="text-left py-3 px-4">Amount</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((don) => (
                        <tr key={don.id} className="border-b" data-testid={`donation-${don.id}`}>
                          <td className="py-3 px-4 font-medium">{don.donor_name}</td>
                          <td className="py-3 px-4 text-muted-foreground">{don.donor_email}</td>
                          <td className="py-3 px-4 font-bold text-green-600">${don.amount?.toFixed(2)}</td>
                          <td className="py-3 px-4">
                            <Badge className={don.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {don.payment_status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {new Date(don.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {donations.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No donations yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Admin Users</CardTitle>
                  <CardDescription>Manage who has access to this dashboard</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div key={u._id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`user-${u._id}`}>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge>{u.role}</Badge>
                          {u._id !== user?.id && (
                            <Button variant="ghost" size="sm" onClick={() => deleteUser(u._id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <UserPlus className="w-5 h-5" />
                    Add Admin User
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createUser} className="space-y-4" data-testid="add-user-form">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-primary hover:bg-primary-hover">
                      Create Admin User
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Alert Settings
                </CardTitle>
                <CardDescription>Configure who receives email notifications for new inquiries and donations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-base font-semibold">Alert Recipients</Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    Email addresses that will receive alerts (email integration pending GoDaddy setup)
                  </p>
                  <div className="space-y-2">
                    {alertSettings?.alert_emails?.map((email) => (
                      <div key={email} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <span>{email}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeAlertEmail(email)}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Input
                      type="email"
                      placeholder="Add email address"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      data-testid="new-alert-email"
                    />
                    <Button onClick={addAlertEmail} data-testid="add-alert-email-btn">
                      Add
                    </Button>
                  </div>
                </div>
                <div className="border-t pt-6">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> Email alerts will be activated once you provide your GoDaddy SMTP credentials. 
                    Until then, all inquiries and donations are stored in the database and visible here.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#home" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Family Life Lifters" className="h-14 w-auto" />
          </a>
          
          <div className="hidden lg:flex items-center gap-8">
            <a href="#about" className={`font-medium transition-colors ${isScrolled ? 'text-accent hover:text-primary' : 'text-white hover:text-secondary'}`} data-testid="nav-about">About</a>
            <a href="#programs" className={`font-medium transition-colors ${isScrolled ? 'text-accent hover:text-primary' : 'text-white hover:text-secondary'}`} data-testid="nav-programs">Programs</a>
            <a href="#services" className={`font-medium transition-colors ${isScrolled ? 'text-accent hover:text-primary' : 'text-white hover:text-secondary'}`} data-testid="nav-services">Services</a>
            <a href="#contact" className={`font-medium transition-colors ${isScrolled ? 'text-accent hover:text-primary' : 'text-white hover:text-secondary'}`} data-testid="nav-contact">Contact</a>
            <Button 
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-6 btn-primary"
              onClick={() => document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-donate-btn"
            >
              <Heart className="w-4 h-4 mr-2" />
              Donate
            </Button>
          </div>
          
          <button 
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-accent' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-accent' : 'text-white'}`} />
            )}
          </button>
        </div>
      </div>
      
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            <a href="#about" className="block text-accent font-medium py-2" onClick={() => setMobileMenuOpen(false)}>About</a>
            <a href="#programs" className="block text-accent font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Programs</a>
            <a href="#services" className="block text-accent font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#contact" className="block text-accent font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <Button 
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-full"
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Heart className="w-4 h-4 mr-2" />
              Donate
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center hero-bg"
      style={{
        backgroundImage: `url(https://images.unsplash.com/photo-1597098415642-079578e90887?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRpdmVyc2UlMjBmYW1pbHklMjBvdXRkb29yc3xlbnwwfHx8fDE3NzQ4Mjc1Mzh8MA&ixlib=rb-4.1.0&q=85)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <p className="text-secondary text-sm uppercase tracking-[0.2em] mb-6 animate-fade-in-up">
          Strengthening Families, Building Communities
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 animate-fade-in-up delay-100">
          Reducing Poverty Through<br />
          <span className="text-secondary">Integrated Support</span>
        </h1>
        <p className="text-white/90 text-lg sm:text-xl max-w-2xl mx-auto mb-10 animate-fade-in-up delay-200">
          Family Life Lifters empowers low-to-moderate income families in Connecticut 
          through comprehensive social and economic programming that strengthens family 
          stability and builds lasting community connections.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
          <Button 
            size="lg"
            className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-lg btn-primary"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="hero-get-started-btn"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            size="lg"
            variant="outline"
            className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-accent rounded-full px-8 py-6 text-lg"
            onClick={() => document.getElementById('programs')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="hero-learn-more-btn"
          >
            Learn More
          </Button>
        </div>
      </div>
      
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronRight className="w-8 h-8 text-white rotate-90" />
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-primary text-sm uppercase tracking-[0.2em] mb-4 font-medium">Who We Are</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-accent mb-6">
              Building Stronger Families, One Community at a Time
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-6">
              Family Life Lifters is a lean, impactful nonprofit organization serving 
              Greater Hartford and surrounding Connecticut communities. Our mission is to 
              reduce poverty through integrated social and economic programming that 
              strengthens family stability and expands access to opportunity.
            </p>
            <p className="text-muted-foreground text-base leading-relaxed mb-8">
              Through our extensive network of community and institutional partners, we 
              deliver comprehensive services including financial empowerment, workforce 
              development, health education, mental wellness support, civic engagement, 
              and family caregiver support.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-6 bg-secondary/20 rounded-3xl">
                <p className="font-heading text-3xl font-bold text-primary mb-1">6</p>
                <p className="text-sm text-muted-foreground">Service Areas</p>
              </div>
              <div className="text-center p-6 bg-secondary/20 rounded-3xl">
                <p className="font-heading text-3xl font-bold text-primary mb-1">CT</p>
                <p className="text-sm text-muted-foreground">Communities Served</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/6646978/pexels-photo-6646978.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Community volunteers helping families"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary rounded-3xl flex items-center justify-center shadow-xl">
              <HandHeart className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Programs Section
const ProgramsSection = () => {
  const programs = [
    {
      id: 'queen-esther',
      title: 'Queen Esther Program',
      subtitle: 'In Partnership with DCF',
      description: 'A collaborative initiative with the Connecticut Department of Children and Families focused on recruiting and supporting foster and kinship families, coordinating training and licensing pathways, and connecting families to critical resources.',
      image: 'https://images.unsplash.com/photo-1596510915005-2cd3c22b37df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxmb3N0ZXIlMjBmYW1pbHklMjBtb3RoZXIlMjBjaGlsZCUyMGxvdmUlMjBjYXJpbmclMjBBZnJpY2FuJTIwQW1lcmljYW58ZW58MHx8fHwxNzc0ODI4NDAxfDA&ixlib=rb-4.1.0&q=85',
      link: 'https://www.queenestherct.org',
      color: 'bg-primary'
    },
    {
      id: 'therapy',
      title: 'Therapy Services',
      subtitle: 'Licensed Professional Support',
      description: 'Comprehensive therapy services for families and children provided by licensed therapists. We offer a safe, supportive environment for addressing mental health needs and building emotional resilience.',
      image: 'https://images.pexels.com/photos/7579304/pexels-photo-7579304.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      color: 'bg-secondary'
    },
    {
      id: 'uplift',
      title: 'Community Uplift Restoration',
      subtitle: 'Holistic Family Support',
      description: 'A comprehensive family stabilization model with six integrated service areas designed to address the root causes of poverty and family instability through coordinated, prevention-focused support.',
      image: 'https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      color: 'bg-accent'
    }
  ];

  return (
    <section id="programs" data-testid="programs-section" className="py-20 lg:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-primary text-sm uppercase tracking-[0.2em] mb-4 font-medium">Our Programs</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-accent mb-6">
            Comprehensive Support for Families
          </h2>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            We focus on three major program areas to provide holistic support for families 
            in our community.
          </p>
        </div>
        
        <div className="space-y-8 lg:space-y-16">
          {programs.map((program, index) => (
            <div 
              key={program.id}
              data-testid={`program-card-${program.id}`}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="aspect-[16/10] rounded-3xl overflow-hidden shadow-xl program-card-image">
                  <img 
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className={`inline-block px-4 py-1 ${program.color} text-white rounded-full text-sm mb-4`}>
                  {program.subtitle}
                </div>
                <h3 className="font-heading text-xl sm:text-2xl font-bold text-accent mb-4">
                  {program.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {program.description}
                </p>
                {program.link ? (
                  <a 
                    href={program.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary font-medium hover:underline"
                    data-testid={`program-link-${program.id}`}
                  >
                    Visit Website
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                ) : (
                  <Button 
                    className="bg-primary hover:bg-primary-hover text-white rounded-full btn-primary"
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid={`program-inquire-${program.id}`}
                  >
                    Inquire Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Bento Grid Section
const ServicesSection = () => {
  const services = [
    {
      id: 'health',
      title: 'Health & Wellness',
      description: 'Promoting physical health through education, resources, and community-based wellness programs.',
      icon: Heart,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'financial',
      title: 'Financial Empowerment',
      description: 'Practical education in budgeting, debt management, credit recovery, and long-term economic resilience.',
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      id: 'civic',
      title: 'Civic Engagement',
      description: 'Empowering community members to participate in civic life and advocate for positive change.',
      icon: Vote,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'mental',
      title: 'Mental & Emotional Wellness',
      description: 'Supporting mental health through counseling, support groups, and wellness resources.',
      icon: Brain,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'workforce',
      title: 'Workforce Development',
      description: 'Career pathways, job training, and employment support to increase income mobility.',
      icon: Briefcase,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      id: 'family',
      title: 'Family & Caregiver Support',
      description: 'Resources and assistance for caregivers, including childcare, respite, and family stabilization services.',
      icon: Users,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50'
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-primary text-sm uppercase tracking-[0.2em] mb-4 font-medium">Uplift Community Restoration</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-accent mb-6">
            Six Integrated Service Areas
          </h2>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            Our comprehensive model addresses interconnected challenges families face, 
            creating a holistic approach to long-term stability and success.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <div 
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="service-card p-8 card-hover bento-item"
              >
                <div className={`w-14 h-14 ${service.bgColor} rounded-2xl flex items-center justify-center mb-6`}>
                  <IconComponent className={`w-7 h-7 ${service.color}`} />
                </div>
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-accent mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Contact/Inquiry Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    program_interest: '',
    referral_source: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success('Thank you for your inquiry! We will be in touch soon.');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        program_interest: '',
        referral_source: '',
        message: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error submitting inquiry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-primary text-sm uppercase tracking-[0.2em] mb-4 font-medium">Get In Touch</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-accent mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-8">
              Whether you're seeking support for your family, interested in volunteering, 
              or want to learn more about our programs, we're here to help. Fill out the 
              form and a member of our team will reach out to you.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-accent mb-1">Location</h4>
                  <p className="text-muted-foreground">Greater Hartford, Connecticut</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-accent mb-1">Email</h4>
                  <p className="text-muted-foreground">info@familylifelifters.org</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-accent mb-1">Phone</h4>
                  <p className="text-muted-foreground">Contact us for more information</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-stone-100">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-accent font-medium">First Name *</Label>
                  <Input 
                    id="first_name"
                    data-testid="input-first-name"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="mt-2 bg-stone-50 border-stone-200 focus:border-primary focus:ring-primary"
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-accent font-medium">Last Name *</Label>
                  <Input 
                    id="last_name"
                    data-testid="input-last-name"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="mt-2 bg-stone-50 border-stone-200 focus:border-primary focus:ring-primary"
                    placeholder="Doe"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-accent font-medium">Email *</Label>
                  <Input 
                    id="email"
                    type="email"
                    data-testid="input-email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-2 bg-stone-50 border-stone-200 focus:border-primary focus:ring-primary"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-accent font-medium">Phone</Label>
                  <Input 
                    id="phone"
                    type="tel"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-2 bg-stone-50 border-stone-200 focus:border-primary focus:ring-primary"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="program_interest" className="text-accent font-medium">Program Interest *</Label>
                <Select 
                  value={formData.program_interest}
                  onValueChange={(value) => setFormData({...formData, program_interest: value})}
                  required
                >
                  <SelectTrigger className="mt-2 bg-stone-50 border-stone-200" data-testid="select-program">
                    <SelectValue placeholder="Select a program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="queen-esther">Queen Esther Program (Foster/Kinship)</SelectItem>
                    <SelectItem value="therapy">Therapy Services</SelectItem>
                    <SelectItem value="financial">Financial Empowerment</SelectItem>
                    <SelectItem value="workforce">Workforce Development</SelectItem>
                    <SelectItem value="health">Health & Wellness</SelectItem>
                    <SelectItem value="mental">Mental & Emotional Wellness</SelectItem>
                    <SelectItem value="civic">Civic Engagement</SelectItem>
                    <SelectItem value="family">Family & Caregiver Support</SelectItem>
                    <SelectItem value="general">General Inquiry</SelectItem>
                    <SelectItem value="volunteer">Volunteer Opportunities</SelectItem>
                    <SelectItem value="partnership">Partnership/Collaboration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="referral_source" className="text-accent font-medium">How did you hear about us? *</Label>
                <Select 
                  value={formData.referral_source}
                  onValueChange={(value) => setFormData({...formData, referral_source: value})}
                  required
                >
                  <SelectTrigger className="mt-2 bg-stone-50 border-stone-200" data-testid="select-referral">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dcf">Department of Children & Families</SelectItem>
                    <SelectItem value="community">Community Organization</SelectItem>
                    <SelectItem value="church">Faith-Based Organization</SelectItem>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="friend">Friend or Family</SelectItem>
                    <SelectItem value="social">Social Media</SelectItem>
                    <SelectItem value="website">Website/Search</SelectItem>
                    <SelectItem value="event">Community Event</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="message" className="text-accent font-medium">Message *</Label>
                <Textarea 
                  id="message"
                  data-testid="input-message"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="mt-2 bg-stone-50 border-stone-200 focus:border-primary focus:ring-primary min-h-[120px]"
                  placeholder="Tell us how we can help you..."
                />
              </div>
              
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-lg btn-primary"
                data-testid="submit-inquiry-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Donate Section with Stripe
const DonateSection = () => {
  const [donationData, setDonationData] = useState({
    name: '',
    email: '',
    amount: '',
    customAmount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDonate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const amount = donationData.amount === 'other' 
      ? parseFloat(donationData.customAmount) 
      : parseFloat(donationData.amount);
    
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid donation amount');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/donations/checkout`, {
        amount: amount,
        donor_name: donationData.name,
        donor_email: donationData.email,
        message: donationData.message,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error creating checkout:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <section id="donate" data-testid="donate-section" className="py-20 lg:py-32 bg-accent text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-secondary text-sm uppercase tracking-[0.2em] mb-4 font-medium">Support Our Mission</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6">
              Help Us Lift More Families
            </h2>
            <p className="text-white/80 text-base leading-relaxed mb-6">
              Your generous donation helps us continue providing essential services to 
              families in need across Connecticut. Every contribution makes a difference 
              in strengthening communities and reducing poverty.
            </p>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="font-heading font-bold text-xl">100%</p>
                <p className="text-white/70 text-sm">Goes to family programs</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-3xl p-8 text-accent">
            <h3 className="font-heading text-xl font-bold mb-6">Make a Donation</h3>
            <form onSubmit={handleDonate} className="space-y-4" data-testid="donation-form">
              <div>
                <Label htmlFor="donor_name" className="text-accent font-medium">Name *</Label>
                <Input 
                  id="donor_name"
                  data-testid="donation-input-name"
                  required
                  value={donationData.name}
                  onChange={(e) => setDonationData({...donationData, name: e.target.value})}
                  className="mt-2 bg-stone-50 border-stone-200"
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label htmlFor="donor_email" className="text-accent font-medium">Email *</Label>
                <Input 
                  id="donor_email"
                  type="email"
                  data-testid="donation-input-email"
                  required
                  value={donationData.email}
                  onChange={(e) => setDonationData({...donationData, email: e.target.value})}
                  className="mt-2 bg-stone-50 border-stone-200"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label className="text-accent font-medium">Donation Amount *</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['25', '50', '100', '250', '500', 'other'].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setDonationData({...donationData, amount: amt})}
                      className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                        donationData.amount === amt 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-stone-200 hover:border-primary/50'
                      }`}
                      data-testid={`donation-amount-${amt}`}
                    >
                      {amt === 'other' ? 'Other' : `$${amt}`}
                    </button>
                  ))}
                </div>
                {donationData.amount === 'other' && (
                  <Input 
                    type="number"
                    min="1"
                    placeholder="Enter amount"
                    value={donationData.customAmount}
                    onChange={(e) => setDonationData({...donationData, customAmount: e.target.value})}
                    className="mt-2 bg-stone-50 border-stone-200"
                    data-testid="donation-custom-amount"
                  />
                )}
              </div>
              <div>
                <Label htmlFor="donor_message" className="text-accent font-medium">Message (Optional)</Label>
                <Textarea 
                  id="donor_message"
                  data-testid="donation-input-message"
                  value={donationData.message}
                  onChange={(e) => setDonationData({...donationData, message: e.target.value})}
                  className="mt-2 bg-stone-50 border-stone-200 min-h-[80px]"
                  placeholder="Leave a message..."
                />
              </div>
              <Button 
                type="submit"
                disabled={isSubmitting || !donationData.amount}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-lg btn-primary"
                data-testid="submit-donation-btn"
              >
                {isSubmitting ? 'Processing...' : 'Donate Now'}
                <Heart className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Secure payment powered by Stripe
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Donation Success Page
const DonationSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/donations/status/${sessionId}`);
      setPaymentInfo(response.data);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <CardContent className="pt-8 pb-8">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600 animate-pulse" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-accent mb-2">Processing...</h2>
              <p className="text-muted-foreground">Confirming your donation</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-accent mb-2">Thank You!</h2>
              <p className="text-muted-foreground mb-4">
                Your donation of ${paymentInfo?.amount?.toFixed(2)} has been received.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Your generosity helps us continue serving families in our community.
              </p>
              <Button asChild className="bg-primary hover:bg-primary-hover">
                <a href="/">Return to Home</a>
              </Button>
            </>
          )}
          {(status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-accent mb-2">Payment Issue</h2>
              <p className="text-muted-foreground mb-6">
                There was an issue with your payment. Please try again.
              </p>
              <Button asChild className="bg-primary hover:bg-primary-hover">
                <a href="/#donate">Try Again</a>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-accent text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <img src={LOGO_URL} alt="Family Life Lifters" className="h-16 w-auto mb-6 brightness-0 invert" />
            <p className="text-white/70 text-sm leading-relaxed">
              Reducing poverty through integrated social and economic programming that 
              strengthens family stability.
            </p>
          </div>
          
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Programs</h4>
            <ul className="space-y-3">
              <li><a href="#programs" className="text-white/70 hover:text-secondary transition-colors footer-link">Queen Esther Program</a></li>
              <li><a href="#programs" className="text-white/70 hover:text-secondary transition-colors footer-link">Therapy Services</a></li>
              <li><a href="#services" className="text-white/70 hover:text-secondary transition-colors footer-link">Community Uplift</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Services</h4>
            <ul className="space-y-3">
              <li><a href="#services" className="text-white/70 hover:text-secondary transition-colors footer-link">Financial Empowerment</a></li>
              <li><a href="#services" className="text-white/70 hover:text-secondary transition-colors footer-link">Workforce Development</a></li>
              <li><a href="#services" className="text-white/70 hover:text-secondary transition-colors footer-link">Health & Wellness</a></li>
              <li><a href="#services" className="text-white/70 hover:text-secondary transition-colors footer-link">Mental Wellness</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-white/70">
                <MapPin className="w-4 h-4" />
                Greater Hartford, CT
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <Mail className="w-4 h-4" />
                info@familylifelifters.org
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} Family Life Lifters. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="/admin/login" className="text-white/50 hover:text-secondary text-sm transition-colors">Admin</a>
            <a href="#" className="text-white/50 hover:text-secondary text-sm transition-colors">Privacy Policy</a>
            <a href="#" className="text-white/50 hover:text-secondary text-sm transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Home Component
const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen">
      <Navigation 
        isScrolled={isScrolled} 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <HeroSection />
      <AboutSection />
      <ProgramsSection />
      <ServicesSection />
      <ContactSection />
      <DonateSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/donation-success" element={<DonationSuccess />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
